import React, { useMemo, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { isEmpty } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { Panel } from 'primereact/panel'
import { Button } from 'primereact/button'

import { Company, LoadBalancerGroup, useLoadBalancerGroupUpdateMutation } from '../../../redux/api/web-api'
import { LBgroupDescriptionValidation } from '../load-balancer-group-form-validation'
import { notifySuccess, notifyError } from '../../notificationsManager/Notify'
import { Input } from '../../../uikit/Input'
import { Dropdown } from '../../../uikit/Dropdown'
import useText from '../../../hooks/useText'

interface LoadBalancerDetailsInputsProps {
    loadBalancerGroup: LoadBalancerGroup
    companies: Company[]
    hasChargers: boolean
}

export const LoadBalancerDetailsInputs = ({
    loadBalancerGroup,
    companies,
    hasChargers,
}: LoadBalancerDetailsInputsProps): React.ReactElement => {
    const text = useText()
    const navigate = useNavigate()

    const schema = yup.object().shape({
        description: LBgroupDescriptionValidation.nullable(),
    })

    const {
        register,
        handleSubmit,
        watch,
        reset,
        control,
        formState: { errors, isValid, isDirty, isSubmitSuccessful },
    } = useForm<LoadBalancerGroup>({
        resolver: yupResolver(schema, { abortEarly: false }),
        mode: 'all',
        defaultValues: {
            description: loadBalancerGroup?.description || '',
            companyId: loadBalancerGroup?.companyId || '',
        },
    })

    const [updateLoadBalancerGroup, { isLoading }] = useLoadBalancerGroupUpdateMutation()

    const changeCompany = watch('companyId')

    const companyOptions = useMemo(() => {
        if (!companies) return []
        return companies.map((company) => ({
            label: company.name || '',
            value: company.id,
        }))
    }, [companies])

    const saveLbgroup = async (formData: LoadBalancerGroup) => {
        let tempLoadBalancerGroup = {
            ...loadBalancerGroup,
            description: formData?.description,
            companyId: formData?.companyId,
        }
        if (!isEmpty(changeCompany) && loadBalancerGroup?.measurementDeviceId) {
            tempLoadBalancerGroup = {
                ...loadBalancerGroup,
                description: formData?.description,
                companyId: formData?.companyId,
                measurementDeviceId: null,
            }
        }
        try {
            await updateLoadBalancerGroup({
                loadBalancerGroup: tempLoadBalancerGroup,
            }).unwrap()
            notifySuccess(text('msgLoadbalancerGroupUpdate'))
        } catch (error) {
            notifyError(text('msgLoadbalancerGroupUpdateError'))
        }
    }

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({}, { keepValues: true })
        }
    }, [isSubmitSuccessful, reset])

    return (
        <Panel toggleable header={text('lblLBInformation')} data-testid="loadbalancer-info">
            <div className="w-full pb-3 mt-1">
                <Controller
                    name="companyId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Dropdown
                            id="organisation"
                            label={text('lblOrganisation')}
                            required
                            showClear={false}
                            options={companyOptions}
                            value={value}
                            onChange={(e) => {
                                onChange(e.value)
                            }}
                            disabled={hasChargers}
                            disabledReason={hasChargers ? text('lblLoadBalancerHasChargePoints') : undefined}
                            endButton={
                                <Button
                                    icon="pi pi-external-link"
                                    onClick={() => navigate(`/company/${changeCompany}`, {})}
                                    disabled={isDirty}
                                    tooltip={isDirty ? undefined : text('btnGoToOrganisation')}
                                    tooltipOptions={{ position: 'top' }}
                                    data-testid={isDirty ? 'go-to-company-disabled' : 'go-to-company'}
                                />
                            }
                        />
                    )}
                />
            </div>
            <div className="w-full pb-3 mt-3">
                <Input
                    type="text"
                    id="description"
                    label={text('lblDescription')}
                    data-testid="description-input"
                    {...register('description')}
                    error={errors.description?.message}
                />
            </div>

            <div className="flex justify-content-end align-items-center pt-3 mt-4">
                <Button
                    label={text('btnSave')}
                    icon="pi pi-save"
                    data-testid="save-button"
                    onClick={handleSubmit(saveLbgroup)}
                    disabled={isLoading || !isValid || !isDirty}
                    loading={isLoading}
                />
            </div>
        </Panel>
    )
}

export default LoadBalancerDetailsInputs
